import seriesHelpers from "./seriesHelpers"

function compareStrings(a, b) {
    return a.localeCompare(b, 'sv-SE')
}

function getErrorMessage(err) {
    if (err.message) {
        return err.message
    } else if (err.code) {
        return err.code
    } else {
        return 'Okänt fel'
    }
}

function getDateTimeString(date) {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    const hour = date.getHours().toString().padStart(2, '0')
    const minute = date.getMinutes().toString().padStart(2, '0')
    return `${year}-${month}-${day} ${hour}:${minute}`
}

function isLeapYear(year) {
    return ((year % 4) === 0 && (year % 100) !== 0) || year % 400 === 0
}

function daysOfMonth(year, month) {
    const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
    if (month == 2 && isLeapYear(year)) {
        return 29
    }
    return days[month - 1]
}

function parseDateTimeString(str) {
    let parts = [-1, -1, -1, -1, -1, -1]
    let part = 0

    for (let i = 0; i < str.length; i++) {
        const c = str.charAt(i)
        if (c >= '0' && c <= '9') {
            if (parts[part] === -1) {
                parts[part] = 0
            } else {
                parts[part] *= 10
            }
            parts[part] += Number.parseInt(c)
        } else {
            part++
            if (part >= parts.length) {
                break
            }
        }
    }

    if (parts[0] < 1970 || parts[0] > 9999) {
        return null
    }

    if (parts[1] === -1) {
        parts[1] = 1
    } else if (parts[1] < 0 || parts[1] > 12) {
        return null
    }

    if (parts[2] === -1) {
        parts[2] = 1
    } else if (parts[2] < 1 || parts[2] > daysOfMonth(parts[0], parts[1])) {
        return null
    }

    if (parts[3] === -1) {
        parts[3] = 0
    } else if (parts[3] > 23) {
        return null
    }

    if (parts[4] === -1) {
        parts[4] = 0
    } else if (parts[4] > 59) {
        return null
    }

    return new Date(`${parts[0]}-${parts[1]}-${parts[2]} ${parts[3]}:${parts[4]}`)
}

function validateEmail(email) {
    return String(email)
        .toLowerCase()
        .match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)
}

function validatePhoneNumber(phone) {
    return String(phone)
        .replaceAll(' ', '')
        .replaceAll('-', '')
        .match(/^((\+46|0)7\d{8}\d*)$/)
}

function toNumber(str, def = NaN) {
    return (str === null || String(str).trim() === '') ? def : Number(String(str).replace(/\s/, '').replace(',', '.'))
}

function formatNumber(nbr, options = null) {
    if (nbr === null || isNaN(nbr)) {
        return options?.invalid ?? ''
    }
    const opt = {}
    if (options?.series) {
        const precision = seriesHelpers.getFieldPrecision(options.series.split('.')[1]) ?? null
        opt.minimumFractionDigits = precision
        opt.maximumFractionDigits = precision
    }
    if (options?.decimals) {
        opt.minimumFractionDigits = options?.decimals
        opt.maximumFractionDigits = options?.decimals
    }
    if (options?.min) {
        opt.minimumFractionDigits = options?.min
    }
    if (options?.max) {
        opt.minimumFractionDigits = options?.max
    }
    opt.useGrouping = options?.group ?? false
    return Number(nbr).toLocaleString('sv', opt)        
}

function numberToString(val, def = null, decimalPlaces = null) {
    if (val == null) {
        return def
    }
    let num = typeof val === 'number' ? val : Number(String(val).replace(',', '.'))
    if (isNaN(num)) {
        return def
    }
    if (decimalPlaces) {
        return num.toLocaleString('sv', { useGrouping: false, minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })
    } else {
        return num.toLocaleString('sv', { useGrouping: false })
    }
}

function stringValue(val) {
    return val ? String(val).trim() : ''
}

function formatPrice(val, def = null) {
    return (val != null && !isNaN(val)) ? Number(val).toLocaleString('sv', { useGrouping: false, minimumFractionDigits: 2 }) : def
}

function requiredString(val, name) {
    const str = val ? String(val).trim() : ''
    if (str) {
        return str
    }
    throw new Error(name + ' saknas')
}

function requiredNumber(val, name) {
    const num = String(val).trim() !== '' ? Number(String(val).replace(',', '.')) : null
    if (num === null) {
        throw new Error(name + ' saknas')
    }
    if (isNaN(num)) {
        throw new Error(name + ' ogiltigt tal')
    }
    return num
}

function requiredInteger(val, name) {
    const num = String(val).trim() !== '' ? Number(String(val).replace(',', '.')) : null
    if (num === null) {
        throw new Error(name + ' saknas')
    }
    if (isNaN(num) || !Number.isInteger(num)) {
        throw new Error(name + ' ogiltigt heltal')
    }
    return num
}

const FUNCTIONS = {
    "AVRUNDA": { params: 2, func: args => Math.round(args[0] * Math.pow(10, args[1])) / Math.pow(10, args[1]) },
    "AVRUNDA_UPP": { params: 2, func: args => Math.ceil(args[0] * Math.pow(10, args[1])) / Math.pow(10, args[1]) },
    "AVRUNDA_NER": { params: 2, func: args => Math.floor(args[0] * Math.pow(10, args[1])) / Math.pow(10, args[1]) },
    "ABS":  { params: 1, func: args => Math.abs(args[0]) },
}

function evaluateExpression(name, input, variables, test = false, callStack = []) {
    let i = 0
    const s = input.replace(/\s+/g, '')
    if (callStack.includes(name)) {
        throw new Error('Cirkulär referens i formler: ' + name)
    }
    callStack.push(name)

    function peek() {
        return s[i]
    }

    function isDigit(c) {
        return c >= '0' && c <= '9'
    }

    function isLetter(c) {
        return c.toLowerCase() != c.toUpperCase()
    }

    function consume(ch) {
        if (s[i] === ch) {
            i++
            return true
        }
        return false
    }

    function getVariableValue(n) {
        if (!Object.hasOwn(variables, n)) {
            throw new Error('Okänd benämning \'' + n + '\'')
        }
        if (variables[n].formula !== null) {
            return evaluateExpression(n, variables[n].formula, variables, test, callStack)
        }
        return variables[n].value
    }

    function getFunctionValue(n, args) {
        if (!Object.hasOwn(FUNCTIONS, n)) {
            throw new Error('Okänd funktion \'' + n + '\'')
        }
        if (args.length != FUNCTIONS[n].params) {
            throw new Error('Funktion \'' + n + '\' behöver ' + FUNCTIONS[n].params + " argument")
        }
        return FUNCTIONS[n].func(args)
    }

    function parseNumber() {
        const start = i

        if (i < s.length && isDigit(s[i])) {
            let sawComma = false

            while (i < s.length) {
                const c = s[i]
                if (isDigit(c)) {
                    i++
                } else if (c === ',' && !sawComma) {
                    sawComma = true
                    i++
                } else {
                    break
                }
            }

            const n = Number(s.slice(start, i).replace(',', '.'))
            if (!Number.isFinite(n)) {
                throw new Error('Ogiltigt tal på position ' + start)
            }
            return n
        }
        if (i < s.length && (isLetter(s[i]) || s[i] === '_')) {
            while (i < s.length) {
                const c = s[i]
                if (isDigit(c) || isLetter(c) || c === '_') {
                    i++
                } else {
                    break
                }
            }
            const end = i
            if (consume('(')) {
                const args = []
                while (!consume(')')) {
                    args.push(parseExpression())
                    if (!consume(';') || s[i] !== ')') {
                        throw new Error('Förväntade ett semikolon eller en slutparentes på position ' + i)
                    }                    
                }
                return getFunctionValue(s.slice(start, end), args)
            }
            return getVariableValue(s.slice(start, i))
        }
        throw new Error('Förväntade ett tal eller en benämning på position ' + i)
    }

    function parsePrimary() {
        if (consume('(')) {
            const val = parseExpression()
            if (!consume(')')) {
                throw new Error('Saknar \')\' på position ' + i)
            }
            return val
        }
        return parseNumber()
    }

    function parseUnary() {
        let result
        if (consume('-')) {
            result = -parseUnary() // unary minus
        } else if (consume('+')) {
            result = parseUnary() // optional unary plus
        } else {
            result = parsePrimary()
        }
        return test ? 1 : result            
    }

    function parseTerm() {
        let left = parseUnary()
        while (true) {
            if (consume('*')) {
                left *= parseUnary()
            } else if (consume('/')) {
                const right = parseUnary()
                if (right === 0) throw new Error('Division med noll')
                left /= right
            } else {
                break
            }
        }
        return test ? 1 : left
    }

    function parseExpression() {
        let left = parseTerm()
        while (true) {
            if (consume('+')) {
                left += parseTerm()
            } else if (consume('-')) {
                left -= parseTerm()
            } else {
                break
            }
        }
        return test ? 1 : left
    }

    consume('=')
    const result = parseExpression()

    if (i !== s.length) {
        throw new Error('Okänt tecken på position ' + i + ': \'' + peek() + '\'')
    }

    callStack.pop()
    return test ? 1 : result
}

function evaluateFormula(name, formula, variables) {
    return evaluateExpression(name.toUpperCase(), formula.toUpperCase(), variables)
}

function testFormula(name, formula, variables) {
    return evaluateExpression(name.toUpperCase(), formula.toUpperCase(), variables, true)
}

export default {
    getErrorMessage,
    compareStrings,
    getDateTimeString,
    parseDateTimeString,
    validateEmail,
    validatePhoneNumber,
    toNumber,
    numberToString,
    stringValue,
    requiredString,
    requiredNumber,
    requiredInteger,
    evaluateFormula,
    testFormula,
    formatPrice,
    formatNumber
}